import React from 'react';
import { Search, X } from 'lucide-react';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    aria-pressed={active}
    onClick={onClick}
    className={`text-sm font-medium px-3.5 py-1.5 rounded-full transition-colors ${
      active ? 'bg-[#B5541B] text-white' : 'bg-[#B5541B]/10 text-[#B5541B] hover:bg-[#B5541B]/20'
    }`}
  >
    {children}
  </button>
);

const ChipRow = ({ label, options, value, onChange, prefix }) => {
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`faq-${prefix}-row`}>
      <span className="text-xs font-semibold uppercase tracking-wide text-[#3B3535]/60 mr-1 w-16 sm:w-auto">{label}</span>
      <Chip active={!value} onClick={() => onChange(null)} testid={`faq-${prefix}-all`}>All</Chip>
      {options.map((o) => (
        <Chip key={o} active={value === o} onClick={() => onChange(value === o ? null : o)} testid={`faq-${prefix}-${slugify(o)}`}>
          {o}
        </Chip>
      ))}
    </div>
  );
};

export const FaqFilters = ({ content, meta, filters, setFilters, hasActiveFilters, onClear }) => {
  const set = (key) => (val) => setFilters((f) => ({ ...f, [key]: val }));
  const typeLabel = (t) => (t === 'Scenario' ? content.typeScenarioLabel : t === 'FAQ' ? content.typeFaqLabel : t);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <label className="relative flex-1 block">
          <Search className="w-4 h-4 text-[#3B3535]/50 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            data-testid="faq-search-input"
            type="search"
            value={filters.query}
            onChange={(e) => set('query')(e.target.value)}
            placeholder={content.searchPlaceholder}
            className="w-full h-11 pl-10 pr-4 rounded-full border border-gray-200 bg-white text-sm text-[#1F1E1E] placeholder:text-[#3B3535]/45 focus:outline-none focus:ring-2 focus:ring-[#B5541B]/40 focus:border-[#B5541B]"
          />
        </label>
        {meta.types.length > 1 && (
          <div className="inline-flex rounded-full bg-[#1F1E1E]/5 p-1 self-start" role="tablist" data-testid="faq-type-toggle">
            <button
              type="button"
              data-testid="faq-type-all"
              aria-pressed={!filters.type}
              onClick={() => set('type')(null)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${!filters.type ? 'bg-[#1F1E1E] text-white' : 'text-[#3B3535] hover:text-[#1F1E1E]'}`}
            >
              {content.typeAllLabel}
            </button>
            {meta.types.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`faq-type-${slugify(t)}`}
                aria-pressed={filters.type === t}
                onClick={() => set('type')(t)}
                className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${filters.type === t ? 'bg-[#1F1E1E] text-white' : 'text-[#3B3535] hover:text-[#1F1E1E]'}`}
              >
                {typeLabel(t)}
              </button>
            ))}
          </div>
        )}
      </div>
      <ChipRow label={content.audienceLabel} options={meta.audiences} value={filters.audience} onChange={set('audience')} prefix="audience" />
      <ChipRow label={content.topicsLabel} options={meta.topics} value={filters.topic} onChange={set('topic')} prefix="topic" />
      {hasActiveFilters && (
        <button
          type="button"
          data-testid="faq-clear-filters"
          onClick={onClear}
          className="inline-flex items-center gap-1 text-sm font-medium text-[#B5541B] hover:underline"
        >
          <X className="w-3.5 h-3.5" /> Clear filters
        </button>
      )}
    </div>
  );
};
